import { TabContext, TabList } from "@mui/lab";
import {
  Box,
  Grid,
  IconButton,
  InputLabel,
  styled,
  Tab,
  TableCell,
  tableCellClasses,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DataNotFound from "../Components/Common/DataNotFound";
import CustomPagination from "../Components/Common/CommonPagination";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  getAllClaims,
  getAllEligibleMembers,
  updateClaim,
  getEnhancedClaims,
  moveClaimStage,
  getStage,
} from "../Redux/Actions/action";
import ErrorHandler, { CommonStatusChip } from "../Utils/handler";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { BucketURL } from "../ApiSetup/Api";
import ButtonContained from "../Components/Common/ButtonContained";
import CommonTextField from "../Components/Common/CommonTextField";
import CustomDialog from "../Components/Common/CustomDialog";
import CommonFileUpload from "../Components/Common/CommonFileUpload";
import TextLabel from "../Components/Common/CommonTextLabel";
import Assets from "../Components/Common/Assets";
import EditIcon from "@mui/icons-material/Edit";
import CommonTable from "../Components/Common/CommonTable";
import { showLoader } from "../Redux/Slices/loaderSlice";
import dayjs from "dayjs";
import CommonRadioButton from "../Components/Common/CommonRadio";
import CommonDropDown from "../Components/Common/CommonDropDown";
import VisibilityIcon from "@mui/icons-material/Visibility";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme?.palette?.bgLightWhite?.main,
    fontSize: 14,
    // fontWeight: "bold",
    textAlign: "center", // Horizontally align content,
    lineHeight: "1.2rem",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "10px 10px !important",
    textAlign: "center", // Horizontally align content
    borderBottom: "2px solid #d9d6f3",
  },
}));

const Claim = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [value, setValue] = useState("0");
  const [data, setData] = useState({});
  const [claimsData, setClaimsData] = useState({});
  const [enhancedClaimsData, setEnhancedClaimsData] = useState({});
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [status, setStatus] = React.useState("Approved");
  const [rejectionReason, setRejectionReason] = React.useState("");
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState("");
  const [stageRemarks, setStageRemarks] = useState("");
  const [filterStage, setFilterStage] = useState("all");

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const tabData = [
    { label: "Eligible Member", value: "0", color: theme.palette.primary.main },
    { label: "Claims Status", value: "1", color: theme.palette.primary.main },
    { label: "Sahay Claims", value: "2", color: theme.palette.success.main },
  ];

  const _getApi = async () => {
    dispatch(showLoader(true));
    let body = {
      page: page,
      limit: rowsPerPage,
    };
    try {
      if (value === "2") {
        // Enhanced stage-based claims
        const params = { page, limit: rowsPerPage };
        if (filterStage && filterStage !== "all") {
          params.status = filterStage;
        }
        const result = await dispatch(getEnhancedClaims(params));
        const error = ErrorHandler(result);
        if (error) {
          setEnhancedClaimsData(result?.payload?.data);
        }
      } else {
        const result = await dispatch(
          value == "0" ? getAllEligibleMembers(body) : getAllClaims(body)
        );
        const error = ErrorHandler(result);
        if (error) {
          setClaimsData(result?.payload?.data);
        }
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _getStages = async () => {
    try {
      const result = await dispatch(getStage());
      const error = ErrorHandler(result, false);
      if (error) {
        setStages(result?.payload?.data?.stages || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleClear = () => {
    setOpen(false);
    setDetailOpen(false);
    setData({});
    setStatus("Approved");
    setRejectionReason("");
    setSelectedStage("");
    setStageRemarks("");
  };

  const _updateClaim = async () => {
    if (status === "Rejected" && !rejectionReason) {
      toast.error("Please enter Remark");
      return;
    }
    dispatch(showLoader(true));
    try {
      let body = {
        claimId: data?._id,
        status: status,
      };
      if (status === "Approved") {
        body.claimApprovedDate = dayjs();
      }
      if (status === "Rejected") {
        body.claimRejectedReason = rejectionReason;
        body.claimRejectedDate = dayjs();
      }
      const result = await dispatch(updateClaim(body));
      const error = ErrorHandler(result);
      if (error) {
        toast.success(result?.payload?.message);
        _getApi();
        handleClear();
      }
    } catch (error) {
      toast.error(error?.message || "An unknown error occurred");
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _moveClaimStage = async () => {
    if (!selectedStage) {
      toast.error("Please select a stage");
      return;
    }
    dispatch(showLoader(true));
    try {
      const body = {
        _id: data?._id,
        stage: selectedStage,
        remarks: stageRemarks,
      };
      const result = await dispatch(moveClaimStage(body));
      const error = ErrorHandler(result);
      if (error) {
        toast.success(result?.payload?.message || "Stage updated successfully");
        _getApi();
        handleClear();
      }
    } catch (error) {
      toast.error(error?.message || "An unknown error occurred");
    } finally {
      dispatch(showLoader(false));
    }
  };

  useEffect(() => {
    _getApi();
  }, [value, page, rowsPerPage, filterStage]);

  useEffect(() => {
    _getStages();
  }, []);

  const headerList =
    value == "0"
      ? [
          "No",
          "PPSK ID",
          "Member Name",
          "Mobile No",
          "Coverage Amount",
          "Start Date",
          "End Date",
          "Remaining Days",
          "Status",
        ]
      : value == "1"
      ? [
          "No",
          "PPSK ID",
          "Member Name",
          "Claim Amount",
          "Death Date",
          "Claim Date",
          "Rejection Reason",
          "Status",
          "Action",
        ]
      : [
          "No",
          "PPSK ID",
          "Deceased Name",
          "Claimant",
          "Death Date",
          "Current Stage",
          "Submitted Date",
          "Action",
        ];

  return (
    <div>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "20px", fontWeight: 500, mb: 2 }}>
          Claim Records
        </Typography>
      </Box>
      <TabContext value={value}>
        <Box mb={1}>
          <TabList
            onChange={(e, newValue) => {
              if (newValue !== undefined) {
                setValue(newValue);
                setPage(1);
              }
            }}
            aria-label="tabs with icons"
            sx={{
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .Mui-selected": {
                background: tabData?.filter((e) => e?.value == value)?.[0]
                  ?.color,
                color: `${theme.palette.common.white} !important`,
              },
            }}
          >
            {tabData?.map((tab, index) => {
              return (
                <Tab
                  key={tab?.value || index}
                  label={`${tab?.label}`}
                  value={tab?.value}
                  sx={{
                    marginRight: "10px",
                    border: `2px solid ${tab.color}`,
                    borderRadius: "10px",
                    color: tab.color,
                    height: "40px",
                    minHeight: "0px",
                  }}
                />
              );
            })}
          </TabList>
        </Box>
        {/* Stage Filter for Sahay Claims */}
        {value === "2" && (
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <CommonDropDown
              text="Filter by Stage"
              name="filterStage"
              value={filterStage}
              onChange={(e) => {
                setFilterStage(e.target.value);
                setPage(1);
              }}
              values={["all", ...stages.map((s) => s.stageName)]}
              width="200px"
            />
            {enhancedClaimsData?.counts && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {enhancedClaimsData.counts.map((c, i) => (
                  <Box
                    key={i}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "16px",
                      bgcolor: filterStage === c.claimStage ? "primary.main" : "grey.200",
                      color: filterStage === c.claimStage ? "white" : "text.primary",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setFilterStage(c.claimStage);
                      setPage(1);
                    }}
                  >
                    {c.claimStage}: {c.count}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
        <Grid item xs={12}>
          <CommonTable headerList={headerList?.filter((e) => e)} isNotSorting>
            {value === "2" ? (
              // Enhanced Sahay Claims Table
              enhancedClaimsData?.claims?.length > 0 ? (
                enhancedClaimsData.claims.map((item, index) => (
                  <TableRow key={item._id || index}>
                    <StyledTableCell align="center">
                      {(index % rowsPerPage) + 1 + (page - 1) * rowsPerPage}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {item?.createdFor?.ppskId || "-"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {item?.deceasedName || item?.createdFor?.fullName || "-"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {item?.claimantName || "-"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {item?.dateOfDeath
                        ? dayjs(item.dateOfDeath).format("DD MMM YYYY")
                        : "-"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <CommonStatusChip label={item?.claimStage || "Submitted"} />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {dayjs(item?.createdAt).format("DD MMM YYYY")}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        justifyContent="center"
                      >
                        <IconButton
                          sx={{ padding: 0 }}
                          onClick={() => {
                            setDetailOpen(true);
                            setData(item);
                          }}
                        >
                          <VisibilityIcon fontSize="small" color="info" />
                        </IconButton>
                        {item?.claimStage !== "Completed" &&
                          item?.claimStage !== "Rejected" && (
                            <IconButton
                              sx={{ padding: 0 }}
                              onClick={() => {
                                setOpen(true);
                                setData(item);
                                setSelectedStage(item?.claimStage || "");
                              }}
                            >
                              <EditIcon fontSize="small" color="success" />
                            </IconButton>
                          )}
                      </Box>
                    </StyledTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={8}>
                    <DataNotFound
                      title="No Sahay Claims Found"
                      icon={
                        <ErrorOutlineIcon
                          color="primary"
                          style={{ fontSize: "3rem" }}
                        />
                      }
                      elevation={0}
                    />
                  </StyledTableCell>
                </TableRow>
              )
            ) : claimsData?.[value == "0" ? "eligibleMembers" : "response"]
              ?.length > 0 ? (
              claimsData?.[value == "0" ? "eligibleMembers" : "response"]?.map(
                (item, index) =>
                  value == "0" ? (
                    <TableRow key={index}>
                      <StyledTableCell align="center">
                        {(index % rowsPerPage) + 1 + (page - 1) * rowsPerPage}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.ppskId}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.fullName}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.phoneNumber}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.claimsDetails?.[0]?.claimAmount
                          ? "₹" +
                            Number(
                              item?.claimsDetails?.[0]?.claimAmount
                            ).toLocaleString("en-IN")
                          : "-"}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {dayjs(item?.policyStatus?.activeAt).format(
                          "DD MMM YYYY"
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {dayjs(item?.policyStatus?.expireAt).format(
                          "DD MMM YYYY"
                        )}
                      </StyledTableCell>
                      <StyledTableCell
                        align="center"
                        sx={{
                          color:
                            item?.policyStatus?.remainingRenewDays > 30
                              ? "black"
                              : item?.policyStatus?.remainingRenewDays > 10
                              ? "rgba(245, 134, 52, 1)"
                              : "rgba(227, 54, 41, 1)",
                        }}
                      >
                        {item?.policyStatus?.remainingRenewDays
                          ? item?.policyStatus?.remainingRenewDays + " Days"
                          : "0 Days"}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {
                          <CommonStatusChip
                            label={
                              !item?.policyStatus?.remainingRenewDays
                                ? "Inactive"
                                : "Active"
                            }
                          />
                        }
                      </StyledTableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={index}>
                      <StyledTableCell align="center">
                        {(index % rowsPerPage) + 1 + (page - 1) * rowsPerPage}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.userDetails?.ppskId}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.userDetails?.fullName}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.claimAmount
                          ? "₹" +
                            Number(item.claimAmount).toLocaleString("en-IN")
                          : "-"}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {dayjs(item?.deathDate).format("DD MMM YYYY")}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {dayjs(item?.createdAt).format("DD MMM YYYY")}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.claimRejectedReason || "-"}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {<CommonStatusChip label={item?.status} />}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.status === "Pending" && (
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap={1}
                            justifyContent={"center"}
                          >
                            <IconButton
                              sx={{ padding: 0 }}
                              onClick={() => {
                                setOpen(true);
                                setData(item);
                              }}
                            >
                              <EditIcon fontSize="small" color="success" />
                            </IconButton>
                          </Box>
                        )}
                      </StyledTableCell>
                    </TableRow>
                  )
              )
            ) : (
              <TableRow>
                <StyledTableCell colSpan={10}>
                  <DataNotFound
                    title={"No Records Found"}
                    icon={
                      <ErrorOutlineIcon
                        color="primary"
                        style={{ fontSize: "3rem" }}
                      />
                    }
                    elevation={0}
                  />
                </StyledTableCell>
              </TableRow>
            )}
          </CommonTable>
          {/* Pagination */}
          {value === "2" ? (
            enhancedClaimsData?.pagination?.total > 0 && (
              <Grid item xs={12}>
                <CustomPagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  count={Math.ceil(enhancedClaimsData?.pagination?.total / rowsPerPage)}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Grid>
            )
          ) : (
            claimsData?.count > 0 && (
              <Grid item xs={12}>
                <CustomPagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  count={Math.ceil(claimsData?.count / rowsPerPage)}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Grid>
            )
          )}
        </Grid>
      </TabContext>

      {/* Dialog for Simple Claims (Tab 1) */}
      {open && value === "1" && (
        <CustomDialog
          isOpen={open}
          handleClose={handleClear}
          title={"PPSK Member"}
          padding={"10px"}
          maxWidth={"sm"}
          action={
            <>
              <ButtonContained
                text={"Update"}
                onClick={_updateClaim}
                minWidth={"100%"}
                marginTop={"10px"}
              />
            </>
          }
        >
          <Grid container spacing={2} xs={12}>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <InputLabel
                sx={{
                  letterSpacing: "0.03em",
                  marginRight: "2px",
                  fontSize: "14px",
                  color: "#222222",
                  whiteSpace: "wrap",
                  marginBottom: "0px",
                }}
              >
                Member Name
              </InputLabel>
              <Typography variant="body1" sx={{}}>
                {data?.userDetails?.fullName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <InputLabel
                sx={{
                  letterSpacing: "0.03em",
                  marginRight: "2px",
                  fontSize: "14px",
                  color: "#222222",
                  whiteSpace: "wrap",
                  marginBottom: "0px",
                }}
              >
                Claims Amount
              </InputLabel>
              <Typography variant="body1" sx={{}}>
                {"₹" + Number(data?.claimAmount).toLocaleString("en-IN")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <CommonRadioButton
                text={"Status"}
                name={"status"}
                options={[
                  { value: "Approved", label: "Approve" },
                  { value: "Rejected", label: "Reject" },
                ]}
                value={status}
                onChange={(e) => setStatus(e?.target?.value)}
              />
            </Grid>
            {status === "Rejected" && (
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <CommonTextField
                  value={rejectionReason}
                  width={"100%"}
                  text="Remark"
                  type="text"
                  onChange={(e) => setRejectionReason(e?.target?.value)}
                  placeholder="Enter a Remark"
                  name="rejectionReason"
                  valid
                  multiline
                  rows={3}
                />
              </Grid>
            )}
          </Grid>
        </CustomDialog>
      )}

      {/* Dialog for Sahay Claims Stage Update (Tab 2) */}
      {open && value === "2" && (
        <CustomDialog
          isOpen={open}
          handleClose={handleClear}
          title={"Update Claim Stage"}
          padding={"10px"}
          maxWidth={"sm"}
          action={
            <>
              <ButtonContained
                text={"Update Stage"}
                onClick={_moveClaimStage}
                minWidth={"100%"}
                marginTop={"10px"}
              />
            </>
          }
        >
          <Grid container spacing={2} xs={12}>
            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                Deceased Name
              </InputLabel>
              <Typography variant="body1">
                {data?.deceasedName || data?.createdFor?.fullName || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                PPSK ID
              </InputLabel>
              <Typography variant="body1">
                {data?.createdFor?.ppskId || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                Claimant
              </InputLabel>
              <Typography variant="body1">
                {data?.claimantName || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                Current Stage
              </InputLabel>
              <CommonStatusChip label={data?.claimStage || "Submitted"} />
            </Grid>
            <Grid item xs={12}>
              <CommonDropDown
                text="Move to Stage"
                name="selectedStage"
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
                values={stages.map((s) => s.stageName)}
                valid
              />
            </Grid>
            <Grid item xs={12}>
              <CommonTextField
                value={stageRemarks}
                width={"100%"}
                text="Remarks (Optional)"
                type="text"
                onChange={(e) => setStageRemarks(e?.target?.value)}
                placeholder="Enter remarks for this stage change"
                name="stageRemarks"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </CustomDialog>
      )}

      {/* Dialog for Viewing Claim Details (Tab 2) */}
      {detailOpen && (
        <CustomDialog
          isOpen={detailOpen}
          handleClose={handleClear}
          title={"Claim Details"}
          padding={"10px"}
          maxWidth={"md"}
        >
          <Grid container spacing={2} xs={12}>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                Deceased Name
              </InputLabel>
              <Typography variant="body1" fontWeight={500}>
                {data?.deceasedName || data?.createdFor?.fullName || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                PPSK ID
              </InputLabel>
              <Typography variant="body1">
                {data?.createdFor?.ppskId || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                Date of Death
              </InputLabel>
              <Typography variant="body1">
                {data?.dateOfDeath ? dayjs(data.dateOfDeath).format("DD MMM YYYY") : "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                Place of Death
              </InputLabel>
              <Typography variant="body1">
                {data?.placeOfDeath || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                Cause of Death
              </InputLabel>
              <Typography variant="body1">
                {data?.causeOfDeath || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                Current Stage
              </InputLabel>
              <CommonStatusChip label={data?.claimStage || "Submitted"} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1, mb: 1 }}>
                Claimant Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                Claimant Name
              </InputLabel>
              <Typography variant="body1">
                {data?.claimantName || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                Relation
              </InputLabel>
              <Typography variant="body1">
                {data?.claimantRelation || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel sx={{ fontSize: "14px", color: "#222222", mb: 0.5 }}>
                Phone
              </InputLabel>
              <Typography variant="body1">
                {data?.claimantPhone || "-"}
              </Typography>
            </Grid>
            {data?.stageHistory?.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1, mb: 1 }}>
                    Stage History
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ maxHeight: "200px", overflowY: "auto" }}>
                    {data.stageHistory.map((history, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1,
                          mb: 1,
                          bgcolor: "grey.100",
                          borderRadius: 1,
                          borderLeft: "3px solid",
                          borderColor: "primary.main",
                        }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {history.stage}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(history.changedAt).format("DD MMM YYYY, hh:mm A")}
                          {history.changedBy?.fullName && ` by ${history.changedBy.fullName}`}
                        </Typography>
                        {history.remarks && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {history.remarks}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </CustomDialog>
      )}
    </div>
  );
};

export default Claim;
